import type { MeetingActionItem, TranscriptEntry, TranscriptParticipant, TranscriptSide } from "@/lib/db-types";

/** 754000 ms blir «12:34»; over en time «1:02:03». */
export function formatOffset(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

/** Innslag som inneholder søketeksten (uavhengig av store/små bokstaver) eller er sagt av en deltaker med det navnet. */
export function filterEntries(entries: readonly TranscriptEntry[], query: string): TranscriptEntry[] {
  const q = query.trim().toLocaleLowerCase("nb-NO");
  if (!q) return [...entries];
  return entries.filter((e) => e.text.toLocaleLowerCase("nb-NO").includes(q) || e.participant.toLocaleLowerCase("nb-NO").includes(q));
}

export interface SpeakingSegment {
  participant: string;
  side: TranscriptSide;
  /** Andel av møtet (0 til 1) hvor segmentet starter og hvor bredt det er. */
  from: number;
  width: number;
}

/** Tidslinjen «hvem snakket»: ett segment per innslag, plassert relativt til møtets lengde. Ukjent deltaker regnes som kunden. */
export function speakingSegments(entries: readonly TranscriptEntry[], participants: readonly TranscriptParticipant[]): SpeakingSegment[] {
  if (entries.length === 0) return [];
  const total = Math.max(1, ...entries.map((e) => e.end_offset_ms));
  const sideOf = new Map(participants.map((p) => [p.name, p.side]));
  return entries.map((e) => ({
    participant: e.participant,
    side: sideOf.get(e.participant) ?? "customer",
    from: Math.min(1, e.start_offset_ms / total),
    width: Math.max(0.002, (Math.max(e.end_offset_ms, e.start_offset_ms) - e.start_offset_ms) / total),
  }));
}

/** Nærmeste innslag ved eller før offset (for å scrolle transkripsjonen til et punkt). -1 når listen er tom. */
export function entryIndexAtOffset(entries: readonly TranscriptEntry[], offsetMs: number): number {
  let best = -1;
  for (let i = 0; i < entries.length; i++) {
    if (entries[i].start_offset_ms <= offsetMs) best = i;
    else break;
  }
  return entries.length === 0 ? -1 : Math.max(0, best);
}

/** Bare punkter Sølvrev gjør kan bli oppfølginger; kundens punkter merkes «Kunden gjør». */
export function canBecomeFollowUp(item: Pick<MeetingActionItem, "side">): boolean {
  return item.side === "solvrev";
}

export function setAllFollowUps(items: readonly MeetingActionItem[], value: boolean): MeetingActionItem[] {
  return items.map((i) => (canBecomeFollowUp(i) ? { ...i, add_follow_up: value } : i));
}

/** Er transkripsjonen forfalt for sletting? Raden slettes av cron, men kan stå en natt etter purge_after. */
export function isPastPurge(purgeAfter: string, now: Date): boolean {
  return new Date(purgeAfter).getTime() <= now.getTime();
}
